import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, Subject, timer } from 'rxjs';
import { catchError, debounce, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { CountryService } from '@emi/shared/data-access';
import * as countryActions from './country-actions';
import { HttpErrorResponse } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import { CountryState } from './country-reducer';
import { getCountryState } from './country-selectors';
import { CountryDto, Filters } from '@emi/shared/models';
import { getSelectedFilters } from '../extraction/extraction-selectors';
import { DataExtractionState } from '../extraction/extraction-reducer';
import { selectSkuSearch, SkuSearchState } from '@emi/shared/state/sku-search';
import { createFilterDto } from '@emi/shared/utils';

@Injectable()
export class CountryEffects {
    private currentState: CountryState;
    private currentFilters: Filters;
    private previousFilters: Filters;
    private searchTermState: string;
    private previousSearchTermState: string;
    private includeSearchPhrase: boolean | undefined;

    public readonly destroy$: Subject<boolean> = new Subject();

    constructor(
        private actions$: Actions,
        private countryService: CountryService,
        private countryStore: Store<CountryState>,
        private store: Store<DataExtractionState>,
        private skuSearchStore: Store<SkuSearchState>
    ) {
        this.countryStore.pipe(select(getCountryState)).subscribe((x) => (this.currentState = x));
        this.skuSearchStore.pipe(select(selectSkuSearch)).subscribe((skuSearchState) => (this.searchTermState = skuSearchState.searchTerm));
    }

    getCountries$ = createEffect(() =>
        this.actions$.pipe(
            ofType(countryActions.getCountries),
            map(() => {
                return countryActions.isLoadingCountries();
            })
        )
    );

    fetchCountries$ = createEffect(() =>
        this.actions$.pipe(
            ofType(countryActions.fetchCountries),
            switchMap(() => {
                this.store.pipe(select(getSelectedFilters)).subscribe((filtersState) => (this.currentFilters = filtersState));
                if (this.currentFilters.countries.length > 0) {
                    if (this.currentFilters === this.previousFilters && this.searchTermState === this.previousSearchTermState) {
                        return of(
                            countryActions.getCountriesSuccess({
                                countries: this.currentState.countries,
                            })
                        );
                    }
                }
                return this.countryService
                    .getCountries(
                        createFilterDto({
                            filterState: new Filters(),
                            searchTerm: this.includeSearchPhrase ? this.searchTermState : undefined,
                        })
                    )
                    .pipe(
                        takeUntil(this.destroy$),
                        map((data: CountryDto) => {
                            this.previousFilters = this.currentFilters;
                            this.previousSearchTermState = this.searchTermState;
                            return countryActions.getCountriesSuccess({
                                countries: data.results.sort((a, b) => (a.name > b.name ? 1 : -1)),
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(countryActions.getCountriesFailure({ error }));
                        })
                    );
            })
        )
    );

    searchCountries$ = createEffect(() =>
        this.actions$.pipe(
            ofType(countryActions.searchCountries),
            debounce((action) => timer(action.delay)),
            filter((action) => action.searchText !== this.currentState.searchText),
            map((action) => {
                if (action.searchText !== '') {
                    const searchedCountries = this.currentState.countries.filter((country) =>
                        country.name.toLowerCase().includes(action.searchText.toLowerCase())
                    );
                    return countryActions.searchCountriesSuccess({
                        countries: searchedCountries,
                        searchText: action.searchText,
                    });
                } else {
                    return countryActions.searchCountriesSuccess(
                      {
                       countries: this.currentState.countries,
                       searchText: ''
                      });
                }
            })
        )
    );

    setIsLoading$ = createEffect(() =>
        this.actions$.pipe(
            ofType(countryActions.isLoadingCountries),
            map(() => {
                return countryActions.fetchCountries();
            })
        )
    );
}
